import { useAtom } from "@dbeining/react-atom";
import { Field, Form, Formik } from "formik";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import utc from "dayjs/plugin/utc";
import { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Container } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { useNavigate, useParams, Link } from "react-router-dom";
import * as Yup from "yup";
import { userAtom } from "../../atoms";
import { useViewport } from "../../contexts";
import { RecordAuditMetaBar } from "../../components/Layout/RecordAuditMetaBar";
import { ResponsiveBreadcrumb } from "../../components/Layout/ResponsiveBreadcrumb";

import { SiteEditFormGrid } from "./SiteEditFormGrid";
import { EllipsisActionMenu } from "../../components/UI/EllipsisActionMenu";
import { LoadingSpinner } from "../../components/common/Loading/LoadingSpinner";
import { ConfirmationModel } from "../../components/features/common";
import {
    EditFormLifecycleActions,
    EditFormSaveCancelButtons,
    EditFormSplitFooter,
    shouldShowEditFormLifecycleActions,
} from "../../components/Forms";
import type { ISite, SiteCreatePayload, SiteUpdatePayload } from "../../customTypings/site/Site";
import { IRole } from "../../customTypings/user/Role";
import { sitesService } from "../../services/sites/sites.service";
import { facilityOperationsService } from "../../services/reference/facilityOperations.service";
import type { RegLookupSelectItem } from "../../components/Forms/RegLookupSelect";
import { useFormAutoFocus } from "@/hooks/common/useFormAutoFocus";
import { useSiteRestoreOnCreateConflict } from "@/hooks/createConflict";
import { FormikUnsavedChangesGuard, type LeaveSaveOutcome } from "@/features/unsavedChanges";
import {
    DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE,
    emptyStringToNull,
    hydratePostalAddressForForm,
    mapPostalAddressToApi,
    nullToEmptyString,
    optionalNullableString,
    Role,
    shouldShowFormFieldError,
    arePayloadsEqual,
    editFormBreadcrumbLabel,
    editPageTitle,
    EntityLabels,
    ListPageTitles,
    SectionLabels,
    getUserFacingErrorMessage,
    isUnchangedUpdateResponse,
    loadingPageTitle,
    logErrorForDev,
    NO_CHANGES_TOAST_MESSAGE,
    showToastError,
    showToastSuccess,
    showToastUnchanged,
    buildRecordLifecycleConfirmProps,
} from "../../utils";
import {
    buildSiteAdminPagedListActions,
    formatSiteToastLabel,
    getSiteAdminActionSuccessMessage,
    type SiteAdminConfirmKind,
} from "../SitesListPage/siteAdminListActions";

dayjs.extend(advancedFormat);
dayjs.extend(utc);

function formatSiteMetaDate(iso: string | null | undefined): string {
    if (!iso) return "";
    return dayjs.utc(iso).local().format("Do MMMM YYYY [at] HH:mm");
}

function formatByLine(name: string | null | undefined): string {
    const t = typeof name === "string" ? name.trim() : "";
    return t.length > 0 ? t : "Unknown";
}

type FormValues = SiteCreatePayload & { id?: string };

/** Matches SitesController Create/Update (site name required, non-whitespace). */
const siteEditValidationSchema = Yup.object({
    id: Yup.string().optional(),
    siteName: Yup.string()
        .transform((v) => (typeof v === "string" ? v.trim() : v))
        .required("Site name is required."),
    addressLine1: Yup.string().trim().required("Address line 1 is required."),
    addressLine2: optionalNullableString,
    addressLine3: optionalNullableString,
    town: optionalNullableString,
    county: optionalNullableString,
    postcode: Yup.string().trim().required("Postcode is required."),
    country: optionalNullableString,
    environmentalPermitNumber: optionalNullableString,
    regFacilityOperationId: Yup.string().nullable().optional(),
    weighbridgeUsedFlag: Yup.boolean().required(),
    siteOperatorName: optionalNullableString,
    reportingActiveFlag: Yup.boolean().required(),
});

type ConfirmKind = SiteAdminConfirmKind | null;

const emptyForm: FormValues = {
    siteName: "",
    addressLine1: "",
    addressLine2: "",
    addressLine3: "",
    town: "",
    county: "",
    postcode: "",
    country: "United Kingdom",
    environmentalPermitNumber: "",
    regFacilityOperationId: "",
    weighbridgeUsedFlag: false,
    siteOperatorName: "",
    reportingActiveFlag: true,
};

const SiteEditPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { width } = useViewport();
    const isNarrowScreen = width < 768;
    const currentUser = useAtom(userAtom);
    const isAdmin = currentUser?.identity.roles.some(
        (role: IRole) => role.name === Role.Admin || role.name === Role.SystemAdmin
    );

    const isNew = !id || id === "new";

    const [loading, setLoading] = useState(!isNew);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [initialValues, setInitialValues] = useState<FormValues>(emptyForm);
    const [metaEnabled, setMetaEnabled] = useState(true);
    const [metaDeleted, setMetaDeleted] = useState(false);
    const [createdAt, setCreatedAt] = useState<string | null>(null);
    const [updatedAt, setUpdatedAt] = useState<string | null>(null);
    const [createdByDisplayName, setCreatedByDisplayName] = useState<string | null>(null);
    const [lastUpdatedByDisplayName, setLastUpdatedByDisplayName] = useState<string | null>(null);
    const [deletedAuditAtUtc, setDeletedAuditAtUtc] = useState<string | null>(null);
    const [deletedByDisplayName, setDeletedByDisplayName] = useState<string | null>(null);
    const [disabledAuditAtUtc, setDisabledAuditAtUtc] = useState<string | null>(null);
    const [disabledByDisplayName, setDisabledByDisplayName] = useState<string | null>(null);
    const [facilityOperationItems, setFacilityOperationItems] = useState<RegLookupSelectItem[]>([]);

    const [confirmKind, setConfirmKind] = useState<ConfirmKind>(null);
    const [liveValidationAfterSubmit, setLiveValidationAfterSubmit] = useState(false);
    const savedUpdatePayloadRef = useRef<SiteUpdatePayload | null>(null);
    const leaveAfterSaveRef = useRef(false);
    const saveOutcomeRef = useRef<LeaveSaveOutcome>(null);

    const restoreOnCreate = useSiteRestoreOnCreateConflict({
        restoreDeletedWithCreateValues: sitesService.restoreDeletedWithCreateValues,
        onRestored: (detail) => navigate(`/sites/${detail.id}`, { replace: true }),
    });

    const formAutoFocusRef = useFormAutoFocus(!loading, [loading, id]);

    const validationSchema = useMemo(() => siteEditValidationSchema, []);

    const headerMenuItem = useMemo(
        () => ({ isEnabled: metaEnabled, isDeleted: metaDeleted }),
        [metaEnabled, metaDeleted]
    );

    const headerMenuActions = useMemo(
        () => buildSiteAdminPagedListActions((kind) => setConfirmKind(kind)),
        []
    );

    const buildSiteBasePayload = (values: FormValues): SiteCreatePayload => {
        const t = (s: string) => (typeof s === "string" ? s.trim() : s);
        const address = mapPostalAddressToApi(values);
        return {
            siteName: t(values.siteName),
            addressLine1: nullToEmptyString(address.addressLine1 ?? values.addressLine1).trim(),
            postcode: nullToEmptyString(address.postcode ?? values.postcode).trim(),
            addressLine2: address.addressLine2,
            addressLine3: address.addressLine3,
            town: address.town,
            county: address.county,
            country: address.country,
            environmentalPermitNumber: emptyStringToNull(values.environmentalPermitNumber),
            regFacilityOperationId: values.regFacilityOperationId?.trim() ? values.regFacilityOperationId.trim() : null,
            weighbridgeUsedFlag: values.weighbridgeUsedFlag,
            siteOperatorName: emptyStringToNull(values.siteOperatorName),
            reportingActiveFlag: values.reportingActiveFlag,
        };
    };

    const buildUpdatePayload = (values: FormValues): SiteUpdatePayload => ({
        ...buildSiteBasePayload(values),
        id: values.id!,
    });

    const buildUpdatePayloadFromDetail = (site: ISite): SiteUpdatePayload =>
        buildUpdatePayload({
            id: site.id,
            siteName: site.siteName,
            ...hydratePostalAddressForForm(site),
            environmentalPermitNumber: nullToEmptyString(site.environmentalPermitNumber),
            regFacilityOperationId: site.regFacilityOperationId ?? "",
            weighbridgeUsedFlag: site.weighbridgeUsedFlag,
            siteOperatorName: nullToEmptyString(site.siteOperatorName),
            reportingActiveFlag: site.reportingActiveFlag,
        });

    const applySiteDetail = (site: ISite) => {
        setInitialValues({
            id: site.id,
            siteName: site.siteName,
            ...hydratePostalAddressForForm(site),
            environmentalPermitNumber: nullToEmptyString(site.environmentalPermitNumber),
            regFacilityOperationId: site.regFacilityOperationId ?? "",
            weighbridgeUsedFlag: site.weighbridgeUsedFlag,
            siteOperatorName: nullToEmptyString(site.siteOperatorName),
            reportingActiveFlag: site.reportingActiveFlag,
        });
        setMetaEnabled(site.isEnabled);
        setMetaDeleted(site.isDeleted);
        setCreatedAt(site.createdAtUtc);
        setUpdatedAt(site.updatedAtUtc);
        setCreatedByDisplayName(site.createdByDisplayName ?? null);
        setLastUpdatedByDisplayName(site.lastUpdatedByDisplayName ?? null);
        setDeletedAuditAtUtc(site.deletedAuditAtUtc ?? null);
        setDeletedByDisplayName(site.deletedByDisplayName ?? null);
        setDisabledAuditAtUtc(site.disabledAuditAtUtc ?? null);
        setDisabledByDisplayName(site.disabledByDisplayName ?? null);
        savedUpdatePayloadRef.current = buildUpdatePayloadFromDetail(site);
    };

    useEffect(() => {
        facilityOperationsService
            .listActive()
            .then(setFacilityOperationItems)
            .catch((e: unknown) => logErrorForDev("SiteEditPage.facilityOperations", e));
    }, []);

    useEffect(() => {
        if (isNew || !id) {
            setLoading(false);
            setInitialValues(emptyForm);
            setCreatedByDisplayName(null);
            setLastUpdatedByDisplayName(null);
            setDeletedAuditAtUtc(null);
            setDeletedByDisplayName(null);
            setDisabledAuditAtUtc(null);
            setDisabledByDisplayName(null);
            return;
        }
        setLoading(true);
        setLoadError(null);
        sitesService
            .getById(id)
            .then(applySiteDetail)
            .catch((e: unknown) => {
                logErrorForDev("SiteEditPage.load", e);
                setLoadError(getUserFacingErrorMessage(e, "Could not load site."));
            })
            .finally(() => setLoading(false));
    }, [id, isNew]);

    const confirmProps = useMemo(
        () =>
            buildRecordLifecycleConfirmProps(
                confirmKind,
                EntityLabels.site,
                initialValues.siteName || `this ${EntityLabels.site.toLowerCase()}`
            ),
        [confirmKind, initialValues.siteName]
    );

    const runMutation = (kind: ConfirmKind) => {
        if (!id || isNew || !kind) return;
        const op =
            kind === "disable"
                ? sitesService.disable(id)
                : kind === "enable"
                  ? sitesService.enable(id)
                  : kind === "softDelete"
                    ? sitesService.softDelete(id)
                    : sitesService.restore(id);
        op.then(() => {
            setConfirmKind(null);
            showToastSuccess(getSiteAdminActionSuccessMessage(kind, initialValues.siteName));
            if (kind === "softDelete") {
                navigate("/sites", { replace: true });
                return;
            }
            return sitesService.getById(id).then(applySiteDetail);
        }).catch((e: unknown) => { setConfirmKind(null); showToastError(getUserFacingErrorMessage(e, DEFAULT_ACTION_FAILED_MESSAGE));
        });
    };

    if (!isAdmin) {
        return (
            <Container fluid className="my-3">
                <Alert variant="warning">You do not have permission to manage sites.</Alert>
            </Container>
        );
    }

    if (loading) {
        return (
            <Container fluid className="my-5 d-flex justify-content-center">
                <LoadingSpinner text={loadingPageTitle(EntityLabels.site)} />
            </Container>
        );
    }

    if (loadError) {
        return (
            <Container fluid className="my-3">
                <Alert variant="danger">{loadError}</Alert>
                <Link to="/sites">Back to sites</Link>
            </Container>
        );
    }

    return (
        <Container fluid>
            <Helmet>
                <title>{editPageTitle(EntityLabels.site, isNew)}</title>
            </Helmet>
            <ResponsiveBreadcrumb
                className="mb-3"
                items={[
                    { text: SectionLabels.operations, to: "/operations" },
                    { text: ListPageTitles.sites, to: "/sites" },
                    { text: editFormBreadcrumbLabel(isNew) },
                ]}
            />

            <div
                className="d-flex align-items-center justify-content-between gap-2 mb-3 w-100"
                style={{
                    borderBottom: "2px solid var(--primary)",
                    paddingBottom: "0.5rem",
                }}
            >
                <h1
                    className="mb-0 flex-grow-1 min-w-0"
                    style={{ borderBottom: "none", paddingBottom: 0 }}
                >
                    {editPageTitle(EntityLabels.site, isNew)}
                </h1>
                {isNarrowScreen && !isNew && Boolean(initialValues.id?.trim()) && (
                    <EllipsisActionMenu
                        id="site-edit-actions-dropdown"
                        ariaLabel="Site actions"
                        actions={headerMenuActions}
                        item={headerMenuItem}
                    />
                )}
            </div>

            {!isNew && (
                <RecordAuditMetaBar
                    formatDate={formatSiteMetaDate}
                    formatByLine={formatByLine}
                    createdAt={createdAt}
                    createdByDisplayName={createdByDisplayName}
                    updatedAt={updatedAt}
                    lastUpdatedByDisplayName={lastUpdatedByDisplayName}
                    isDeleted={metaDeleted}
                    isEnabled={metaEnabled}
                    deletedAuditAtUtc={deletedAuditAtUtc}
                    deletedByDisplayName={deletedByDisplayName}
                    disabledAuditAtUtc={disabledAuditAtUtc}
                    disabledByDisplayName={disabledByDisplayName}
                />
            )}

            <Formik<FormValues>
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                validateOnMount={false}
                validateOnChange
                validateOnBlur={false}
                onSubmit={async (values, { resetForm }) => {
                    if (metaDeleted) return;
                    saveOutcomeRef.current = null;
                    setLiveValidationAfterSubmit(true);
                    try {
                        const base = buildSiteBasePayload(values);
                        if (isNew) {
                            try {
                                const created = await sitesService.create(base);
                                setLiveValidationAfterSubmit(false);
                                showToastSuccess(`${formatSiteToastLabel(base.siteName)} created successfully!`);
                                navigate(`/sites/${created.id}`, { replace: true });
                            } catch (err: unknown) {
                                if (restoreOnCreate.handleCreateError(err)) {
                                    restoreOnCreate.setPendingRestoreForm(base);
                                    setLiveValidationAfterSubmit(false);
                                    return;
                                }
                                showToastError(getUserFacingErrorMessage(err, DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE));
                            }
                        } else if (values.id) {
                            const updatePayload = buildUpdatePayload(values);
                            if (arePayloadsEqual(updatePayload, savedUpdatePayloadRef.current)) {
                                setLiveValidationAfterSubmit(false);
                                saveOutcomeRef.current = "unchanged";
                                showToastUnchanged(NO_CHANGES_TOAST_MESSAGE);
                                if (leaveAfterSaveRef.current) return;
                                return;
                            }
                            const result = await sitesService.update(updatePayload);
                            if (isUnchangedUpdateResponse(result)) {
                                setLiveValidationAfterSubmit(false);
                                saveOutcomeRef.current = "unchanged";
                                showToastUnchanged(result.message ?? NO_CHANGES_TOAST_MESSAGE);
                                if (leaveAfterSaveRef.current) return;
                                return;
                            }
                            setLiveValidationAfterSubmit(false);
                            saveOutcomeRef.current = "success";
                            savedUpdatePayloadRef.current = updatePayload;
                            showToastSuccess(`${formatSiteToastLabel(base.siteName)} updated successfully!`);
                            resetForm({ values });
                            if (leaveAfterSaveRef.current) return;
                            navigate("/sites");
                        }
                    } catch (e: unknown) { saveOutcomeRef.current = "failure";
                        showToastError(getUserFacingErrorMessage(e, DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE));
                    }
                }}
            >
                {({ isSubmitting, values, errors, touched, handleChange, setFieldValue, setFieldError, setFieldTouched, setStatus, submitCount, handleSubmit, validateForm, setTouched }) => {
                    const hasPersistedRecord = Boolean(values.id?.trim());
                    const showErr = (field: keyof FormValues) =>
                        shouldShowFormFieldError(
                            errors,
                            field as keyof typeof errors,
                            hasPersistedRecord,
                            submitCount,
                            liveValidationAfterSubmit,
                            touched
                        );
                    const form = (
                    <Form
                        ref={formAutoFocusRef}
                        noValidate
                        onSubmit={handleSubmit}
                    >
                        {metaDeleted && (
                            <Alert variant="warning" className="mb-3">
                                This Site is deleted. Restore it before you make any changes.
                            </Alert>
                        )}

                        <SiteEditFormGrid
                            values={values}
                            errors={errors}
                            touched={touched}
                            handleChange={handleChange}
                            setFieldValue={setFieldValue}
                            setFieldError={setFieldError}
                            setFieldTouched={setFieldTouched}
                            setStatus={setStatus}
                            showErr={showErr}
                            metaDeleted={metaDeleted}
                            isNew={isNew}
                            facilityOperationItems={facilityOperationItems}
                        />

                        <EditFormSplitFooter
                            start={
                                shouldShowEditFormLifecycleActions(isNew, values.id, isNarrowScreen) ? (
                                    <div className="d-flex flex-wrap gap-2">
                                        <EditFormLifecycleActions
                                            metaDeleted={metaDeleted}
                                            metaEnabled={metaEnabled}
                                            onAction={setConfirmKind}
                                        />
                                    </div>
                                ) : undefined
                            }
                        >
                            <EditFormSaveCancelButtons
                                isSubmitting={isSubmitting}
                                disabled={metaDeleted}
                                readOnlyForm={metaDeleted}
                                isNewRecord={isNew}
                                newRecordLabel={EntityLabels.site}
                                enableSaveBeforeLeave={!isNew}
                                leaveAfterSaveRef={leaveAfterSaveRef}
                                saveOutcomeRef={saveOutcomeRef}
                                onCancel={() => navigate("/sites")}
                            />
                        </EditFormSplitFooter>
                    </Form>
                    );

                    return (
                        <FormikUnsavedChangesGuard
                            enableRouteBlocking
                            readOnlyForm={metaDeleted}
                            isNewRecord={isNew}
                            newRecordLabel={EntityLabels.site}
                            enableSaveBeforeLeave={!isNew}
                            leaveAfterSaveRef={leaveAfterSaveRef}
                            saveOutcomeRef={saveOutcomeRef}
                        >
                            {form}
                        </FormikUnsavedChangesGuard>
                    );
                }}
            </Formik>

            <ConfirmationModel
                show={confirmKind !== null}
                onClose={(confirmed) => {
                    if (confirmed) runMutation(confirmKind);
                    else setConfirmKind(null);
                }}
                title={confirmProps.title}
                body={confirmProps.body}
                buttonSuccessVariant={confirmProps.buttonSuccessVariant}
                buttonSuccessText={confirmProps.buttonSuccessText}
                buttonCancelText="Cancel"
            />

            <ConfirmationModel
                show={restoreOnCreate.restoreModalProps.show}
                onClose={restoreOnCreate.confirmRestore}
                title={restoreOnCreate.restoreModalProps.title}
                body={restoreOnCreate.restoreModalProps.body}
                buttonSuccessVariant="primary"
                buttonSuccessText={restoreOnCreate.restoreModalProps.buttonSuccessText}
                buttonCancelText={restoreOnCreate.restoreModalProps.buttonCancelText}
            />
        </Container>
    );
};

export { SiteEditPage };
